import { useApiClient } from "@/hooks/useApiClient";
import useSWR from "swr";
import { useEffect } from "react";
import { toast } from "react-toastify";

interface CompanyListAPIRequest {
  matchType: string;
  matchMethod: string;
  todouhuken: string;
  shyozai: string;
  kubun: string;
  companyName?: string;
  corporateNumber?: string;
}

export interface CompanyListAPIResponseBody {
  id: number;
  corporateNumber: string;
  companyName: string;
  location: string;
  closed: boolean;
}

/** 一括検索 API における明細ごとの取得失敗情報 */
export interface CompanyListBulkFailure {
  corporateNumber: string;
  status: string;
}

export interface CompanyListAPIResponse {
  status: string;
  detail: string;
  body: CompanyListAPIResponseBody[];
  /** 一括検索 API のみ。取得に失敗した明細（会社法人等番号とその失敗理由 status） */
  failures?: CompanyListBulkFailure[];
}

/** 一括検索で全件失敗または一部のみ成功したときの付帯情報 */
export interface BulkSearchIssue {
  /** true: 成功分あり（一部失敗） / false: 全件失敗 */
  isPartialSuccess: boolean;
  detail: string;
  failures: CompanyListBulkFailure[];
}

export interface UseCompanyListResult {
  data: CompanyListAPIResponseBody[];
  isLoading: boolean;
  bulkSearchIssue?: BulkSearchIssue;
}

const errorMessages: Record<string, string> = {
  "400":
    "検索エラー（入力内容に誤りがある、検索結果が多すぎる等）が発生しました。検索条件を変更して再度請求してください。",
  "408": "サイトが混雑しているため取得できませんでした。再度請求してください",
  "500": "サイトが混雑しているため取得できませんでした。再度請求してください",
  "503":
    "民事法務協会サイトがサービス時間外のため取得できませんでした。営業時間内で再度請求してください",
};

/** status に対応するエラー文言を返す。未知の status の場合は undefined */
export const getErrorMessage = (status: string): string | undefined =>
  errorMessages[status];

export const handleError = (status: string): boolean => {
  const message = getErrorMessage(status);
  if (message !== undefined) {
    toast.error(message);
    return true;
  }
  return false;
};

export const useCompanyList = (
  fetchParams: CompanyListAPIRequest | null
): UseCompanyListResult => {
  const { apiClient } = useApiClient();
  const fetcher = async (
    url: string,
    params: CompanyListAPIRequest
  ): Promise<CompanyListAPIResponse> => {
    const { data } = await apiClient.get<CompanyListAPIResponse>(url, {
      params,
    });
    return data;
  };

  const { data, isLoading } = useSWR<CompanyListAPIResponse>(
    fetchParams ? ["/commercial-picture-request/search/", fetchParams] : null,
    async ([url, params]: [string, CompanyListAPIRequest]) =>
      await fetcher(url, params),
    { revalidateOnFocus: false }
  );

  // toast はレンダー本体ではなく data 確定時の effect でのみ発火させる（親の再レンダーで重複発火しないように）
  useEffect(() => {
    if (data === undefined) return;
    if (handleError(data.status)) return;
    if (data.status === "200" && data.body.length === 0) {
      toast.info("検索結果が0件です。");
    }
  }, [data]);

  // データがまだ取得されていない場合に動作
  if (data === undefined) return { data: [], isLoading };

  // スクレイピングシステムでエラーがあった場合
  if (getErrorMessage(data.status) !== undefined) {
    return { data: [], isLoading: false };
  }

  // 検索結果が0だった場合
  if (data.status === "200" && data.body.length === 0) {
    return { data: [], isLoading: false };
  }

  return { data: data.body, isLoading: false };
};
